import { useRef, useCallback, useEffect } from "react";
import type { Rect } from "@/hooks/useCropSelection";

interface CropOverlayProps {
  selection: Rect | null;
  containerRef: React.RefObject<HTMLDivElement | null>;
  onMouseDown: (x: number, y: number) => void;
  onMouseMove: (x: number, y: number) => void;
  onMouseUp: () => void;
  onMouseLeave: () => void;
}

export function CropOverlay({
  selection,
  containerRef,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onMouseLeave,
}: CropOverlayProps) {
  const isDragging = useRef(false);

  const clientToLocal = useCallback(
    (clientX: number, clientY: number) => {
      const el = containerRef.current;
      if (!el) return { x: 0, y: 0 };
      const rect = el.getBoundingClientRect();
      return {
        x: clientX - rect.left,
        y: clientY - rect.top,
      };
    },
    [containerRef]
  );

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      const { x, y } = clientToLocal(e.clientX, e.clientY);
      onMouseMove(x, y);
    };
    const handleUp = () => {
      isDragging.current = false;
      onMouseUp();
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [clientToLocal, onMouseMove, onMouseUp]);

  return (
    <div
      className="absolute inset-0 cursor-crosshair"
      onMouseDown={(e) => {
        if (e.button !== 0) return;
        const { x, y } = clientToLocal(e.clientX, e.clientY);
        isDragging.current = true;
        onMouseDown(x, y);
      }}
      onMouseLeave={onMouseLeave}
      aria-hidden
    >
      {selection && selection.width > 0 && selection.height > 0 && (
        <div
          className="absolute border-2 border-primary bg-primary/10"
          style={{
            left: selection.x,
            top: selection.y,
            width: selection.width,
            height: selection.height,
          }}
        />
      )}
    </div>
  );
}
